import React, { useEffect, useLayoutEffect, useRef } from 'react';

import Button from 'components/partials/button/button';
import IconButton from 'components/partials/button/icon-button';

import Icons from 'assets/icons';

interface AutofillPopoverProps {
  value: number;
  direction: 'row' | 'column';
  unitType: 'amount' | 'discount';
  secondaryLabel: string;
  isRestrictedToRemainder: boolean;
  anchorRef: React.RefObject<HTMLElement | null>;
  popoverRef: React.RefObject<HTMLDivElement | null>;
  onApply: () => void;
  onDismiss: () => void;
  cancelBlur: () => void;
}

const TABLE_SELECTOR = '[data-testid="award-calculation-tab-content"]';
const POPOVER_GAP_PX = 4;

const formatValue = (
  value: number,
  unitType: 'amount' | 'discount',
  secondaryLabel: string
): string =>
  unitType === 'amount' || secondaryLabel === '$'
    ? `$${value.toLocaleString('en-US')}`
    : `${value}${secondaryLabel}`;

const findNextTableInput = (
  anchorEl: HTMLElement | null
): HTMLInputElement | null => {
  const table = anchorEl?.closest(TABLE_SELECTOR);

  if (!table) return null;

  const inputs = Array.from(table.querySelectorAll<HTMLInputElement>('input'));
  const current = anchorEl?.querySelector('input');
  const idx = current ? inputs.indexOf(current) : -1;

  return inputs[idx + 1] ?? null;
};

const AutofillPopover = ({
  value,
  direction,
  unitType,
  secondaryLabel,
  isRestrictedToRemainder,
  anchorRef,
  popoverRef,
  onApply,
  onDismiss,
  cancelBlur,
}: AutofillPopoverProps): React.JSX.Element => {
  const internalRef = useRef<HTMLDivElement>(null);
  const ref = popoverRef ?? internalRef;

  useEffect(() => {
    const el = ref.current;

    if (!el) return;

    const buttons = el.querySelectorAll<HTMLButtonElement>('button');
    const firstButton = buttons[0] ?? null;
    const lastButton = buttons[buttons.length - 1] ?? null;

    const focusAnchorInput = () => {
      const input = anchorRef.current?.querySelector('input');

      if (!input) return;

      input.focus();
      cancelBlur();
    };

    const handleMouseDown = (e: MouseEvent) => {
      e.preventDefault();
      cancelBlur();
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onDismiss();
        focusAnchorInput();

        return;
      }

      if (e.key !== 'Tab') return;

      if (e.shiftKey && document.activeElement === firstButton) {
        e.preventDefault();
        focusAnchorInput();

        return;
      }

      if (!e.shiftKey && document.activeElement === lastButton) {
        e.preventDefault();
        onDismiss();

        const next = findNextTableInput(anchorRef.current);

        if (next) {
          next.focus();
          cancelBlur();
        }
      }
    };

    el.addEventListener('mousedown', handleMouseDown);
    el.addEventListener('keydown', handleKeyDown);

    return () => {
      el.removeEventListener('mousedown', handleMouseDown);
      el.removeEventListener('keydown', handleKeyDown);
    };
  }, [ref, anchorRef, onDismiss, cancelBlur]);

  useLayoutEffect(() => {
    const anchor = anchorRef.current;
    const popover = ref.current;

    if (!anchor || !popover) return;

    const parent = popover.offsetParent as HTMLElement | null;

    if (!parent) return;

    const anchorRect = anchor.getBoundingClientRect();
    const parentRect = parent.getBoundingClientRect();

    const top = anchorRect.bottom - parentRect.top + POPOVER_GAP_PX;
    const left = anchorRect.left - parentRect.left;
    const maxLeft = parentRect.width - popover.offsetWidth;

    popover.style.top = `${top}px`;
    popover.style.left = `${Math.max(0, Math.min(left, maxLeft))}px`;
  });

  const formattedValue = formatValue(value, unitType, secondaryLabel);
  const actionLabel = direction === 'row' ? 'Apply to row' : 'Apply to column';
  const scopeLabel = isRestrictedToRemainder
    ? `remaining ${direction}`
    : `entire ${direction}`;

  return (
    <div
      ref={ref}
      className="absolute z-200 flex items-center gap-3 bg-white border border-navy-200 rounded-lg shadow-md px-3 py-2 whitespace-nowrap"
      role="dialog"
      aria-label={`Autofill suggestion: apply ${formattedValue} to ${scopeLabel}`}
      aria-live="assertive"
      data-testid="autofill-popover"
    >
      <span className="text-sm text-gray-700">
        Apply <strong>{formattedValue}</strong> to {scopeLabel}?
      </span>
      <Button
        variant="brand-filled"
        size="xs"
        onClick={onApply}
        data-testid="autofill-apply-button"
      >
        {actionLabel}
      </Button>
      <IconButton
        variant="reserve-text"
        size="sm"
        icon={<Icons.Close />}
        onClick={onDismiss}
        aria-label="Dismiss autofill suggestion"
        data-testid="autofill-dismiss-button"
      />
    </div>
  );
};

export default AutofillPopover;
